import { FC, useState } from 'react'
import Swal from 'sweetalert2'
import {
  AssinaturaType,
  AtendimentoEstado,
  AtendimentoType,
  downloadArquivo,
  IconPaths,
  ModalSize,
  OptionsHelper,
  PrescricaoSituation,
  showAlertLoading,
  showConfirmAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  AtendimentoList,
  AtendimentoView,
  PrescricaoAtendimentoView,
} from '../../../../atendimento/core/_models'
import {
  downloadRelatorioAtendimento,
  downloadRelatorioTeleconsultoria,
  getAtendimentoById,
} from '../../../../atendimento/core/_requests'
import { teleconsultoriaDisponivel } from '../../../../pareceres/pareceres-list/core/situacaoInterconsulta'
import { RowActions, useModal, useToast } from '../../../../../../components'
import {
  AnotacaoModal,
  IniciarAtendimentoModal,
  AtendimentoModal,
  CancelarAtendimentoModal,
  MarcarFaltaModal,
  MarcarPresencaModal,
  ReabrirAtendimentoModal,
} from '../../component/modal'
import { ExamesModal } from '../../../../atendimento-ASO/components/modal'
import { assumeAtendimento } from '../../core/_requests'
import { useAuth } from '../../../../../auth'
import { temVinculoMedicoAtivoNaEmpresa } from 'app/modules/auth/core/vinculos'
import { getUnidades } from '../../../../unidades/unidades-list/core/_requests'
import { ObservacaoModal } from '../../component/modal/ObservacaoModal'
import { usePrescricaoUtils } from '../../../prescricao/utils/usePrescricaoUtils'
import { DadosSusModal } from '../../../prescricao/components/sus/DadosSusModal'
import { PrescricaoView } from '../../../prescricao/core/_model'
import { getPrescricaoById } from '../../../prescricao/core/_requests'
import { useCertificado } from '../../../../../../helpers/hooks/useCertificado'
import { useNavigate } from 'react-router-dom'
import { useChamadaAtiva } from '../../../chamada-ativa/ChamadaAtivaProvider'
import { rotaDaChamada } from '../../../chamada-ativa/core/_models'
import { rotaParecerInterconsulta } from '../../../parecer-interconsulta/core/rotas'

type Props = {
  atendimento: AtendimentoList
}

/**
 * Espera com loading só se demorar: busca rápida não pisca o alerta na tela (abre e fecha em
 * milissegundos). Fecha o loading ao terminar, com sucesso ou erro.
 */
const ESPERA_ANTES_DO_LOADING_MS = 400
const comLoadingSeDemorar = async <T,>(busca: Promise<T>): Promise<T> => {
  let mostrou = false
  const timer = setTimeout(() => {
    mostrou = true
    showAlertLoading()
  }, ESPERA_ANTES_DO_LOADING_MS)
  try {
    return await busca
  } finally {
    clearTimeout(timer)
    if (mostrou) Swal.close()
  }
}

/** Dados do modal de LME/APAC, fixados no clique (o modal não reinicia se a linha mudar). */
type GeracaoSus = {
  lme: PrescricaoView[]
  apac: PrescricaoView[]
  tipo: AssinaturaType
  atendimento: AtendimentoView
}

/** Prescrições assinadas que ainda não geraram LME/APAC. */
const pendentesLME = (prescricoes: PrescricaoAtendimentoView[] = []) =>
  prescricoes.filter(
    (p) => !p.lme && p.prescricaoLme && p.situacao === PrescricaoSituation.Assinada
  )
const pendentesAPAC = (prescricoes: PrescricaoAtendimentoView[] = []) =>
  prescricoes.filter(
    (p) => !p.apac && p.prescricaoApac && p.situacao === PrescricaoSituation.Assinada
  )

/**
 * Ações do atendimento. As condições de cada item vêm da própria linha (estado, presença,
 * instituição) — a lista é atualizada em tempo real pelo socket. Só LME/APAC buscam o atendimento
 * completo, no clique, porque dependem das prescrições.
 */
export const AtendimentoMedicoActionsCell: FC<Props> = ({ atendimento }) => {
  const { showModal } = useModal()
  const { showToast } = useToast()
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const { encerrarChamada, atendimentoId: chamadaAtivaId } = useChamadaAtiva()
  const { fetchAllGeradas, showPrescricoes } = usePrescricaoUtils()
  const { ensurePassword } = useCertificado()
  const [geracaoSus, setGeracaoSus] = useState<GeracaoSus | null>(null)
  // A linha não traz as prescrições: o atendimento completo é buscado quando o menu abre e
  // reaproveitado no clique de "Gerar LME/APAC" (sem buscar de novo).
  const [atendimentoSus, setAtendimentoSus] = useState<AtendimentoView | null>(null)
  const [falhaAoConferirSus, setFalhaAoConferirSus] = useState(false)
  const [conferindoSus, setConferindoSus] = useState(false)

  const atendimentoId = Number(atendimento.id)
  const encerrarChamadaDoAtendimento = () => encerrarChamada(atendimentoId)
  const estado = atendimento.estado?.id
  const nomePaciente = atendimento.paciente?.pessoa?.nome ?? 'paciente'

  const isMedico = temVinculoMedicoAtivoNaEmpresa(
    currentUser?.vinculos,
    atendimento.paciente?.empresa?.id
  )

  const finalizado = estado === AtendimentoEstado.Finalizado
  const atendeSUS = !!(atendimento.empresa?.sus || atendimento.unidade?.sus)
  const podeGerarSus = finalizado && isMedico && atendeSUS
  // Sem a consulta, as duas ações aparecem: o clique busca de novo e mostra o erro, se houver.
  const pendenciasSus = falhaAoConferirSus
    ? { lme: true, apac: true }
    : atendimentoSus && {
        lme: pendentesLME(atendimentoSus.prescricoes).length > 0,
        apac: pendentesAPAC(atendimentoSus.prescricoes).length > 0,
      }

  /** Ao abrir o menu: "Gerar LME/APAC" só aparecem se ainda há receita assinada pendente. */
  const conferirPendenciasSus = async () => {
    if (!podeGerarSus) return
    setAtendimentoSus(null)
    setFalhaAoConferirSus(false)
    setConferindoSus(true)
    try {
      setAtendimentoSus(await getAtendimentoById(atendimentoId))
    } catch {
      setFalhaAoConferirSus(true)
    } finally {
      setConferindoSus(false)
    }
  }

  const interconsulta = atendimento.tipo === AtendimentoType.Interconsulta

  // Interconsulta tem tela própria: solicitação, exames, parecer e diagnóstico juntos.
  const showAtendimento = () =>
    interconsulta
      ? navigate(rotaParecerInterconsulta(atendimentoId))
      : showModal({
          title: 'Atendimento Médico',
          content: (
            <IniciarAtendimentoModal
              onRetomarChamada={
                chamadaAtivaId === atendimentoId
                  ? () => navigate(rotaDaChamada(atendimentoId))
                  : undefined
              }
            />
          ),
          fetchers: { atendimento: () => getAtendimentoById(atendimentoId) },
        })

  const showExames = () =>
    showModal({
      title: `Exames de ${nomePaciente} - ${atendimento.protocolo}`,
      content: <ExamesModal atendimentoId={atendimentoId} />,
      size: ModalSize.Large,
    })

  const showAnotacao = () =>
    showModal({
      title: estado === AtendimentoEstado.Pendente ? 'Resolver Pendência' : 'Adicionar Pendência',
      content: (
        <AnotacaoModal
          atendimentoId={atendimentoId}
          isPendente={estado === AtendimentoEstado.Pendente}
        />
      ),
    })

  const showCancelarAtendimento = () =>
    showModal({
      title: `Cancelar atendimento de ${nomePaciente}?`,
      content: (
        <CancelarAtendimentoModal
          atendimentoId={atendimentoId}
          onAtendimentoEncerrado={encerrarChamadaDoAtendimento}
        />
      ),
      size: ModalSize.Medium,
    })

  const showFaltouAtendimento = () =>
    showModal({
      title: `Marcar falta do paciente ao atendimento de ${nomePaciente}?`,
      content: (
        <MarcarFaltaModal
          atendimentoId={atendimentoId}
          onAtendimentoEncerrado={encerrarChamadaDoAtendimento}
        />
      ),
      size: ModalSize.Medium,
    })

  const showReabrirAtendimento = () =>
    showModal({
      title: `Reabrir o atendimento de ${nomePaciente}?`,
      content: <ReabrirAtendimentoModal atendimentoId={atendimentoId} />,
      size: ModalSize.Medium,
    })

  const showMarcarPresenca = () =>
    showModal({
      title: 'Atendimento',
      content: <MarcarPresencaModal />,
      fetchers: { atendimento: () => getAtendimentoById(atendimentoId) },
    })

  const handleEditAtendimento = () =>
    showModal({
      title: 'Editar Atendimento',
      content: <AtendimentoModal emAtendimento={estado === AtendimentoEstado.EmAtendimento} />,
      fetchers: {
        atendimento: () => getAtendimentoById(atendimentoId),
        unidades: async () => {
          const resp = await getUnidades(
            `page=0&size=100&search=&idEmpresa=${atendimento.empresa?.id}`
          )
          return OptionsHelper.createOptions(resp.data || [], 'nome', 'id')
        },
      },
    })

  const createObservacao = () =>
    showModal({
      title: 'Criar Observação',
      content: <ObservacaoModal atendimentoId={atendimentoId} />,
      size: ModalSize.Medium,
    })

  const baixarAtendimento = async () => {
    try {
      showAlertLoading()
      const arquivo = await downloadRelatorioAtendimento(atendimentoId)
      Swal.close()
      downloadArquivo(arquivo)
    } catch (error) {
      showError(error)
    }
  }

  const baixarTeleconsultoria = async () => {
    try {
      showAlertLoading()
      const arquivo = await downloadRelatorioTeleconsultoria(atendimentoId)
      Swal.close()
      downloadArquivo(arquivo)
    } catch (error) {
      showError(error)
    }
  }

  const assumirAtendimento = async () => {
    const confirmed = await showConfirmAlert({
      title: 'Assumir atendimento?',
      text: `Você passa a ser o profissional de saúde do atendimento de ${nomePaciente}.`,
      confirmButtonText: 'Assumir',
      cancelButtonText: 'Cancelar',
    })
    if (!confirmed) return
    try {
      await assumeAtendimento(atendimentoId)
      showToast({ variant: 'success', title: 'Atendimento assumido.' })
    } catch (error) {
      showError(error)
    }
  }

  /**
   * LME e APAC: busca as prescrições pendentes no clique, libera o certificado e abre o mesmo
   * modal da tela de prescrição (meses do LME, CID, anamnese, termo e quantidades da APAC).
   */
  const gerarDocumentos = async (tipo: 'LME' | 'APAC') => {
    try {
      // Reaproveita o que o menu acabou de buscar; só busca de novo se a conferência falhou.
      const completo =
        atendimentoSus ?? (await comLoadingSeDemorar(getAtendimentoById(atendimentoId)))
      const pendentes =
        tipo === 'LME' ? pendentesLME(completo.prescricoes) : pendentesAPAC(completo.prescricoes)
      if (!pendentes.length) {
        showToast({ variant: 'info', title: `Nenhuma prescrição assinada pendente de ${tipo}.` })
        return
      }
      // O modal lê medicamentos e exames de cada prescrição: a view do atendimento não os traz.
      const prescricoes = await comLoadingSeDemorar(
        Promise.all(pendentes.map((p) => getPrescricaoById(p.id)))
      )
      const certificado = await ensurePassword()
      Swal.close()
      if (!certificado.ok || !certificado.tipo) return
      setGeracaoSus({
        lme: tipo === 'LME' ? prescricoes : [],
        apac: tipo === 'APAC' ? prescricoes : [],
        tipo: certificado.tipo,
        atendimento: completo,
      })
    } catch (error) {
      Swal.close()
      showError(error)
    }
  }

  /** Gerou: fecha e mostra os PDFs atualizados, como antes. */
  const concluirGeracaoSus = async () => {
    const ids = [...(geracaoSus?.lme ?? []), ...(geracaoSus?.apac ?? [])].map((p) => p.id)
    setGeracaoSus(null)
    setAtendimentoSus(null)
    showPrescricoes(await fetchAllGeradas(ids))
  }

  return (
    <>
      {geracaoSus && (
        <DadosSusModal
          aberto
          prescricoesLme={geracaoSus.lme}
          prescricoesApac={geracaoSus.apac}
          tipo={geracaoSus.tipo}
          atendimento={geracaoSus.atendimento}
          // A linha é atualizada pelo socket; o modal não depende da recarga.
          onRecarregar={async () => undefined}
          onGerado={concluirGeracaoSus}
          onDepois={() => setGeracaoSus(null)}
        />
      )}
      <RowActions
        onOpen={conferirPendenciasSus}
        loading={conferindoSus}
        itemName={`atendimento de ${nomePaciente}`}
        primaryAction={{
          label: interconsulta ? 'Abrir parecer' : 'Visualizar',
          onClick: showAtendimento,
          icon: IconPaths.Search,
        }}
        items={[
          {
            label: 'Editar',
            onClick: handleEditAtendimento,
            hidden: !(
              estado === AtendimentoEstado.Aguardando || estado === AtendimentoEstado.EmAtendimento
            ),
          },
          {
            label: 'Assumir atendimento',
            onClick: assumirAtendimento,
            hidden: !(
              isMedico &&
              (estado === AtendimentoEstado.EmAtendimento ||
                estado === AtendimentoEstado.Pendente ||
                estado === AtendimentoEstado.RetornoPendencia)
            ),
          },
          {
            label: 'Criar observação',
            onClick: createObservacao,
            hidden: estado !== AtendimentoEstado.Aguardando,
          },
          {
            label: 'Marcar presença',
            onClick: showMarcarPresenca,
            // Falsy (e não `!== null`): na listagem o campo pode vir ausente em vez de null.
            hidden: Boolean(atendimento.presencaPacienteFormatada),
          },
          { label: 'Inserir exames', onClick: showExames, hidden: finalizado },
          estado === AtendimentoEstado.Pendente
            ? { label: 'Resolver pendência', onClick: showAnotacao }
            : { label: 'Marcar como pendente', onClick: showAnotacao },
          {
            label: 'Baixar atendimento',
            onClick: baixarAtendimento,
            hidden: !(finalizado && isMedico),
            divider: true,
          },
          {
            label: 'Baixar relatório da teleconsultoria',
            onClick: baixarTeleconsultoria,
            hidden: !teleconsultoriaDisponivel(atendimento.situacaoInterconsulta),
          },
          {
            label: 'Gerar APAC',
            onClick: () => gerarDocumentos('APAC'),
            hidden: !(podeGerarSus && pendenciasSus?.apac),
          },
          {
            label: 'Gerar LME',
            onClick: () => gerarDocumentos('LME'),
            hidden: !(podeGerarSus && pendenciasSus?.lme),
          },
          {
            label: 'Reabrir atendimento',
            onClick: showReabrirAtendimento,
            hidden:
              estado === AtendimentoEstado.EmAtendimento ||
              estado === AtendimentoEstado.Pendente ||
              estado === AtendimentoEstado.Aguardando,
          },
          {
            label: 'Marcar falta do paciente',
            onClick: showFaltouAtendimento,
            hidden: finalizado,
            divider: true,
          },
          {
            label: 'Cancelar atendimento',
            onClick: showCancelarAtendimento,
            hidden: estado === AtendimentoEstado.Cancelado,
            danger: true,
          },
        ]}
      />
    </>
  )
}
